import * as THREE from 'three'
import type { EntityHighlightTarget } from '../types/entity-highlight-target'
import {
  collectImportedModelRenderableMeshTargets,
  expandImportedModelRenderableTargets,
  type ImportedModelResolvedTarget,
} from './ImportedAssemblyPartTargetResolver'
import { ImportedModelObjectMetadataService } from '../metadata'

interface GlbHierarchyMetaLike {
  hasDirectGeometry?: boolean
  highlightTargets?: EntityHighlightTarget[]
}

interface GlbHierarchyLike {
  meta?: GlbHierarchyMetaLike
}

/**
 * 解析 imported-model body leaf 对应的精确高亮目标。
 *
 * 设计说明：
 * 1. body leaf 允许一个语义节点映射到多个 mesh target；
 * 2. resolver 只负责把 hierarchy.meta.highlightTargets 解成精确对象，不处理渲染细节；
 * 3. 当显式目标失效时回退到语义根下的 mesh targets，保证后续 controller 至少有稳定可见的兜底结果。
 */
export class ImportedBodyLeafTargetResolver {
  /**
   * 解析 body leaf 的精确目标列表。
   *
   * @param targetObject body leaf 对应的场景对象。
   * @returns 精确 target 列表；可能为 1 对多。
   */
  public resolve(targetObject: THREE.Object3D): ImportedModelResolvedTarget[] {
    const semanticRoot = this.resolveSemanticRoot(targetObject)
    const highlightTargets = this.readHighlightTargets(targetObject)
    const resolvedTargets = this.resolveExplicitTargets(semanticRoot, targetObject, highlightTargets)

    if (resolvedTargets.length > 0) {
      return resolvedTargets
    }

    return collectImportedModelRenderableMeshTargets(semanticRoot)
  }

  /**
   * 读取 body leaf 上的 highlightTargets。
   *
   * @param targetObject body leaf 对象。
   * @returns 可解析的目标数组。
   */
  private readHighlightTargets(targetObject: THREE.Object3D): EntityHighlightTarget[] {
    const hierarchy = ImportedModelObjectMetadataService.resolveHierarchy(targetObject) as GlbHierarchyLike | undefined
    return Array.isArray(hierarchy?.meta?.highlightTargets) ? hierarchy.meta.highlightTargets : []
  }

  /**
   * 找到最近的语义根。
   *
   * @param targetObject 输入对象。
   * @returns 具备直接几何的语义根。
   */
  private resolveSemanticRoot(targetObject: THREE.Object3D): THREE.Object3D {
    let current: THREE.Object3D | null = targetObject
    while (current) {
      const hierarchy = ImportedModelObjectMetadataService.resolveHierarchy(current) as GlbHierarchyLike | undefined
      if (hierarchy?.meta?.hasDirectGeometry) {
        return current
      }
      current = current.parent
    }
    return targetObject
  }

  /**
   * 把显式高亮目标映射为实际场景对象。
   *
   * @param semanticRoot 语义根。
   * @param fallbackRoot 回退查找根。
   * @param highlightTargets 显式高亮目标。
   * @returns 成功命中的精确对象列表。
   */
  private resolveExplicitTargets(
    semanticRoot: THREE.Object3D,
    fallbackRoot: THREE.Object3D,
    highlightTargets: EntityHighlightTarget[],
  ): ImportedModelResolvedTarget[] {
    const resolvedTargets: ImportedModelResolvedTarget[] = []

    highlightTargets.forEach((highlightTarget) => {
      const matchedObject =
        this.findObjectByIdInSubtree(semanticRoot, highlightTarget.objectId)
        ?? this.findObjectByIdInSubtree(fallbackRoot, highlightTarget.objectId)
      if (!matchedObject) {
        return
      }

      resolvedTargets.push(
        ...expandImportedModelRenderableTargets(
          matchedObject,
          this.normalizePrimitiveIndices(highlightTarget.primitiveIndices),
        ),
      )
    })

    return resolvedTargets
  }

  /**
   * 在目标子树中按 uuid 查找对象。
   *
   * @param root 查找根节点。
   * @param objectId 对象 uuid。
   * @returns 命中的对象。
   */
  private findObjectByIdInSubtree(root: THREE.Object3D, objectId: string): THREE.Object3D | null {
    if (root.uuid === objectId) {
      return root
    }
    return root.getObjectByProperty('uuid', objectId) ?? null
  }

  /**
   * 归一化 primitiveIndices。
   *
   * @param primitiveIndices primitive 下标集合。
   * @returns 清洗后的下标数组。
   */
  private normalizePrimitiveIndices(primitiveIndices?: number[]): number[] | undefined {
    if (!Array.isArray(primitiveIndices) || primitiveIndices.length === 0) {
      return undefined
    }

    const normalized = primitiveIndices.filter(
      (primitiveIndex) => Number.isInteger(primitiveIndex) && primitiveIndex >= 0,
    )

    return normalized.length > 0 ? normalized : undefined
  }
}
